import { createGraphClient } from '~/client/graph'
import CommonContainer from '~/client/src/shared/CommonContainer'
import type { IAppConfig } from '~/client/src/shared/Config'
import ApiAuthService from '~/client/src/shared/services/ApiAuthService'
import ProcoreService from '~/client/src/shared/services/ProcoreService'
import StruxHubAuthService from '~/client/src/shared/services/StruxHubAuthService'
import registerCommonEvents from '~/client/src/shared/stores/EventStore/commonEvents'
import EffectsProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/EffectsProcessor'
import FlowProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/FlowProcessor/FlowProcessor'
import GraphProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/GraphProcessors/GraphProcessor'
import WorkerProcessor from '~/client/src/shared/stores/EventStore/EffectsProcessors/WorkerProcessor/WorkerProcessor'
import InitialState from '~/client/src/shared/stores/InitialState'
import GraphExecutor from '~/client/src/shared/utils/GraphExecutor'
import { getReturnToTenantHost } from '~/client/src/shared/utils/loginPortal'

import Configuration from './LoginConfig'
import handleLoginAuthEntry from './stores/EventStore/loginAuthEntryHandler'
import registerLoginEvents from './stores/EventStore/loginEvents'
import LoginEventsStore from './stores/EventStore/LoginEvents.store'
import LoginRootStore from './stores/LoginRoot.store'

export default class Container extends CommonContainer {
  private configuration: IAppConfig = Configuration
  private $rootStore: LoginRootStore
  private initialState: InitialState
  protected eventsStore: LoginEventsStore
  private struxHubAuthService: StruxHubAuthService
  private apiAuthService: ApiAuthService
  private procoreService: ProcoreService
  private graphExecutor: GraphExecutor

  public constructor(private isTestEnv: boolean) {
    super()

    this.initialState = new InitialState()
    this.procoreService = new ProcoreService(this.initialState)
    this.graphExecutor = new GraphExecutor(this.initialState)
    this.apiAuthService = new ApiAuthService(
      this.initialState,
      this.graphExecutor,
      this.configuration.AUTH_EXTERNAL_URL,
    )
    this.struxHubAuthService = new StruxHubAuthService(this.apiAuthService)

    const getValidToken = () => this.struxHubAuthService.getValidAccessToken()
    const forceRefreshToken = () =>
      this.struxHubAuthService.refreshAccessToken()

    // Route REST refreshes (ApiAuthService.updateAccessToken) through the
    // GraphQL service's dedup gate so concurrent REST + GraphQL 401s share a
    // single network refresh instead of racing and revoking each other.
    this.apiAuthService.setRefreshAccessTokenDelegate(forceRefreshToken)

    // QR-code signup (BaseSignUp.store, SignUp.store) calls into Apollo
    // from the login bundle. login.struxhub.com itself has no /graphql;
    // the QR redirect carries `?return_to=<tenant-url>` so the signup
    // operations can talk to the source tenant's graph (the tenant is
    // its hostname). Without a URL the client lands on the transportless
    // `uri: null` branch in client/graph/index.ts and the queries
    // silently fail. Cross-origin is fine: server CORS is AllowAnyOrigin
    // and the SignUp + CompaniesBasicInfo fields are `.AllowAnonymous()`
    // on the graph schema, so Bearer null is accepted. When there's no
    // destination (manual navigation to login.struxhub.com/project/X/
    // sign-up) we leave the client transportless and BaseSignUp surfaces
    // a clear error.
    this.initialState.graphClient = createGraphClient(
      getValidToken,
      forceRefreshToken,
      getReturnToTenantHost() ?? undefined,
    )

    this.eventsStore = new LoginEventsStore(
      new EffectsProcessor(
        getValidToken,
        new FlowProcessor(),
        new WorkerProcessor(),
        new GraphProcessor(this.initialState, this.graphExecutor),
      ),
      this.initialState,
    )

    registerCommonEvents(this.eventsStore, this.rootStore, {
      handleAuthEntry: handleLoginAuthEntry,
    })
    registerLoginEvents(this.eventsStore, this.rootStore)

    this.registerGlobalEventListeners()
  }

  public get rootStore() {
    if (!this.$rootStore) {
      this.$rootStore = new LoginRootStore(
        this.configuration,
        this.initialState,
        this.eventsStore,
        this.apiAuthService,
        this.struxHubAuthService,
        this.procoreService,
        this.graphExecutor,
        this.isTestEnv,
      )
    }

    return this.$rootStore
  }
}
